import { useQuery } from "@tanstack/react-query";
import { Post } from "../../../utils/apiService";
import {
  GetCheckedInOutVisitorsBody,
  GetCurrentlyOverStayedVisitorsBody,
  GetOverStayedVisitorsBody,
  GetPendingApprovedVisitorsBody,
  GetTotalVisitorsCountByDateParams,
  GetVisitorsCountBody,
  GetVisitorsCountByNationalityResponse,
  GetVisitorsCountByVisitTypeResponse,
} from "../interface";

export const FETCH_DASHBOARD_DETAILS_KEYS = {
  FETCH_VISITORS_COUNT: "FETCH_VISITORS_COUNT",
  FETCH_VISITORS_COUNT_BY_VISIT_TYPE: "FETCH_VISITORS_COUNT_BY_VISIT_TYPE",
  FETCH_VISITORS_COUNT_BY_NATIONALITY: "FETCH_VISITORS_COUNT_BY_NATIONALITY",
  FETCH_TOTAL_VISITORS_COUNT_BY_DATE: "FETCH_TOTAL_VISITORS_COUNT_BY_DATE",
  FETCH_PENDING_APPROVED_VISITORS: "FETCH_PENDING_APPROVED_VISITORS",
  FETCH_CURRENTLY_OVERSTAYED_VISITORS: "FETCH_CURRENTLY_OVERSTAYED_VISITORS",
  FETCH_CHECKED_IN_VISITORS: "FETCH_CHECKED_IN_VISITORS",
  FETCH_CHECKED_OUT_VISITORS: "FETCH_CHECKED_OUT_VISITORS",
  FETCH_OVERSTAYED_VISITORS: "FETCH_OVERSTAYED_VISITORS",
};

const getVisitorsCountAction = async (body: GetVisitorsCountBody) => {
  return Post({
    url: "/api/Dashboard/GetCounts",
    body,
  });
};

export const useGetVisitorsCount = (body: GetVisitorsCountBody) => {
  return useQuery({
    queryKey: [FETCH_DASHBOARD_DETAILS_KEYS.FETCH_VISITORS_COUNT, body],
    queryFn: () => getVisitorsCountAction(body),
  });
};

const getVisitorsCountByVisitTypeAction = async () => {
  return Post({
    url: "/api/Dashboard/GetVisitorCountByVisitType",
  });
};

export const useGetVisitorsCountByVisitType = () => {
  return useQuery<GetVisitorsCountByVisitTypeResponse[]>({
    queryKey: [FETCH_DASHBOARD_DETAILS_KEYS.FETCH_VISITORS_COUNT_BY_VISIT_TYPE],
    queryFn: () => getVisitorsCountByVisitTypeAction(),
  });
};

const getVisitorsCountByNationalityAction = async () => {
  return Post({
    url: "/api/Dashboard/GetVisitorCountByNationality",
  });
};

export const useGetVisitorsCountByNationality = () => {
  return useQuery<GetVisitorsCountByNationalityResponse[]>({
    queryKey: [
      FETCH_DASHBOARD_DETAILS_KEYS.FETCH_VISITORS_COUNT_BY_NATIONALITY,
    ],
    queryFn: () => getVisitorsCountByNationalityAction(),
  });
};

const getTotalVisitorsCountByDateAction = async (
  params: GetTotalVisitorsCountByDateParams
) => {
  return Post({
    url: "/api/Dashboard/GetVisitorsCount",
    params,
  });
};

export const useGetTotalVisitorsCountByDate = (
  params: GetTotalVisitorsCountByDateParams
) => {
  return useQuery({
    queryKey: [
      FETCH_DASHBOARD_DETAILS_KEYS.FETCH_TOTAL_VISITORS_COUNT_BY_DATE,
      params,
    ],
    queryFn: () => getTotalVisitorsCountByDateAction(params),
  });
};

const getPendingApprovedVisitorsAction = async (
  body: GetPendingApprovedVisitorsBody
) => {
  return Post({
    url: "/api/Dashboard/GetVisitorsList",
    body,
  });
};

export const useGetPendingApprovedVisitors = (
  body: GetPendingApprovedVisitorsBody
) => {
  return useQuery({
    queryKey: [
      FETCH_DASHBOARD_DETAILS_KEYS.FETCH_PENDING_APPROVED_VISITORS,
      body.pageNumber,
      body.pageSize,
      body.search,
      body.type,
      body.VisitDurationType,
      body.filterRoleID
    ],
    queryFn: () => getPendingApprovedVisitorsAction(body),
  });
};

const getCurrentlyOverStayedVisitorsAction = async (
  body: GetCurrentlyOverStayedVisitorsBody
) => {
  return Post({
    url: "/api/Reports/GetCurrentlyOverStayedVisitors",
    body,
  });
};

export const useGetCurrentlyOverStayedVisitors = (
  body: GetCurrentlyOverStayedVisitorsBody
) => {
  return useQuery({
    queryKey: [
      FETCH_DASHBOARD_DETAILS_KEYS.FETCH_CURRENTLY_OVERSTAYED_VISITORS,
      body.pageNumber,
      body.pageSize,
      body.search,
    ],
    queryFn: () => getCurrentlyOverStayedVisitorsAction(body),
  });
};

const getOverStayedVisitorsAction = async (body: GetOverStayedVisitorsBody) => {
  return Post({
    url: "/api/Reports/GetOverStayedVisitors",
    body,
  });
};

export const useGetOverStayedVisitors = (body: GetOverStayedVisitorsBody) => {
  return useQuery({
    queryKey: [FETCH_DASHBOARD_DETAILS_KEYS.FETCH_OVERSTAYED_VISITORS, body],
    queryFn: () => getOverStayedVisitorsAction(body),
  });
};

const getCheckedInVisitorsAction = async (
  body: GetCheckedInOutVisitorsBody
) => {
  return Post({
    url: "/api/Reports/GetCheckedInVisitors",
    body,
  });
};

export const useGetCheckedInVisitors = (body: GetCheckedInOutVisitorsBody) => {
  return useQuery({
    queryKey: [FETCH_DASHBOARD_DETAILS_KEYS.FETCH_CHECKED_IN_VISITORS, body],
    queryFn: () => getCheckedInVisitorsAction(body),
  });
};

const getCheckedOutVisitorsAction = async (
  body: GetCheckedInOutVisitorsBody
) => {
  return Post({
    url: "/api/Reports/GetCheckedOutVisitors",
    body,
  });
};

export const useGetCheckedOutVisitors = (body: GetCheckedInOutVisitorsBody) => {
  return useQuery({
    queryKey: [FETCH_DASHBOARD_DETAILS_KEYS.FETCH_CHECKED_OUT_VISITORS, body],
    queryFn: () => getCheckedOutVisitorsAction(body),
  });
};
